import {
    defaultTo,
    concat,
    addIndex,
    ascend,
    eqProps,
    contains,
    placeholder,
    groupBy,
    hasIn,
    toPairs,
    sort,
    findIndex,
    propEq,
    take,
    prop,
    split,
    pluck,
    flatten,
    modifyEntries,
    update,
    pick,
    uniq,
    map,
    isNil,
    differenceWith,
    pipe,
    insert,
    reject,
    createEntry,
    getTotalPayments,
    getPaymentIDs
} from 'core/utils';

export const asisFactory = (state, action) => action.payload;
export const defaultToNull = (state, action) => defaultTo(null, state);
export const defaultToEmptyArr = (state, action) => defaultTo([], state);
export const defaultToEmptyTasks = (state, action) => defaultTo({ value : [] }, state);
export const defaultToEmptyTaskLists = (state, action) => defaultTo({ value : [] }, state);
export const defaultToEmptyObj = (state, action) => defaultTo({}, state);
export const defaultToEmptyLayout = (state, action) => defaultTo({
    taskList : {
        position : 0
    },
    search : {
        show : false   
    },
    entryDialogs : {
        showAddEntry : false,        
        showEditEntry : false,        
        showEditEntryPayments : false
    },
    uplifts : {
        currentNav : 'uplifts'
    },
    entries : {
        currentNav : 'entries',
        display : 'list'
    }
}, state);

export { createLoginStateFactory } from './state/login-state.factory';

export const createUpdateEntryStateFactory = (state, action) => {
    let entry = action.payload;
    let index = findIndex(propEq('id', entry.id), state);
    return update(index, entry, state);
};

export const createRemoveEntryStateFactory = (state, action) => {
    let entry = action.payload;
    let parentEntry = state.find(propEq('id', entry.parent));
    if(parentEntry) {
        parentEntry.payments = reject(eqProps('id', entry), parentEntry.payments);
    }
    
    let idList = [entry.id, ...getPaymentIDs(entry.payments)];
    let rejectPred = pipe(
        prop('id'),
        contains(placeholder, idList)
    );
    let mod = reject(rejectPred);
    return modifyEntries(mod)(state);
};

export const createInsertEntryStateFactory = (state, action) => {
    let { task, entry } = action.payload;
    let newEntry = createEntry(task, entry);

    let mod = insert(newEntry.order, newEntry);
    return modifyEntries(mod)(state);
};

export const createAddEntryStateFactory = (state, action) => {
    let { task, entry } = action.payload;
    let newEntry = createEntry(task, entry);
    return [
        ...state,
        newEntry
    ]; 
};

export const createTaskListsStateFactory = (state, action) => {
    return action.payload.value;
};

export const createUpdateTasksStateFactory = (state, action) => {
    let tasks = action.payload.value;
    let currentTasks = state.value;
    let removed = tasks.filter(hasIn('@removed'));
    let added = differenceWith((x, y) => x.id === y.id, tasks, currentTasks);
    let updated = tasks.filter((task) => {
        let isRemovedOrAdded = [...removed, ...added].find(eqProps('id', task));
        return !isRemovedOrAdded;
    });
    let updatedTasks = pipe(
        concat(added),
        reject(
            ((task) => {
                return removed.find(eqProps('id', task));
            })
        ),
        map((task) => {
            let updatedTask = updated.find(eqProps('id', task));
            return updatedTask ? updatedTask : task;
        })
    )
    return {
        ...action.payload,
        value : updatedTasks(currentTasks)
    };
};

export const createResetTasksStateFactory = (state, action) => {
    return {
        value : []
    };
};

export const createEntryAdsStateFactory = (state, action) => {
    let ads = [
        {
            title : 'Dos and donts of debt relief',
            url : 'https://www.consolidatedcreditcanada.ca/financial-news/dos-and-donts-of-debt-relief/'
        },
        {
            title : 'Is debt consolidation right for you?',
            url : 'https://www.consolidatedcreditcanada.ca/debt-consolidation/'
        },
        {
            title : 'Five ways to improve financial wellness',
            url : 'https://www.consolidatedcreditcanada.ca/financial-news/fast-ways-to-improve-financial-wellness/'
        },
        {
            title : 'Change your mindset and payoff your debt',
            url : 'https://www.consolidatedcreditcanada.ca/financial-news/change-your-mindset-and-pay-off-debt/'
        },
        {
            title : 'Discover debt counselling',
            url : 'https://www.consolidatedcreditcanada.ca/financial-news/debt-counselling-debt-management-plan/'
        },
        {
            title : 'How do I choose the best credit card',
            url : 'https://www.consolidatedcreditcanada.ca/videos/how-do-i-choose-the-best-credit-card/'
        },
        {
            title : 'What is a credit report',
            url : 'https://www.consolidatedcreditcanada.ca/videos/what-is-a-credit-report/'
        },
        {
            title : 'Establishing good credit in Canada',
            url : 'https://www.consolidatedcreditcanada.ca/videos/establishing-good-credit-in-canada/'
        },
        {
            title : 'How to improve your credit score',
            url : 'https://www.consolidatedcreditcanada.ca/videos/how-to-improve-your-credit-score/'
        },
        {
            title : "How much debt is too much",
            url : 'https://www.consolidatedcreditcanada.ca/videos/how-much-debt-is-too-much/'
        }
    ];

    return pipe(
        addIndex(map)((entry, index) => {
            let adIndex = Math.floor(Math.random() * ads.length);
            let ad = ads[adIndex];
            let entryAd;
            if(index % 5 === 0) {
                entryAd = {
                    id : entry.id,
                    ...ad
                };
            }
            return entryAd
        }),
        reject(isNil),
    )(action.payload);
};

export const createSetEntriesStateFactory = (state, action) => {
    const entries = (action.payload.entries || []).map((entry) => ({
        ...entry,
        amount: Number(entry.amount),
        date: entry.date || entry.dueDate || null,
        dueDate: entry.dueDate || null,
        reminderDate: entry.reminderDate || null,
        isReminderOn: entry.isReminderOn || false,
        payments: entry.payments || []
    }));
    return sort(ascend(prop('order')), entries);
};

export const createTotalsViewModel = (tasks) => {
    let viewModel = tasks
        .filter((task) => !task.complete && task.ignore === false)
        .reduce((agg, item) => {
            let { amount, type } = item;
            amount = Number(amount);
            let totalPayments = getTotalPayments(item);
            amount = totalPayments > amount ? 0 : amount - totalPayments;
            return {
                ...agg,
                credit : agg.credit + (type === 'CREDIT' ? amount : 0),
                debit : agg.debit + (type === 'DEBIT' ? amount : 0)
            };
        }, { credit : 0, debit : 0, total : 0 });

    let completedAmount = tasks
                                .filter((task) => task.type === 'DEBIT')
                                .reduce((agg, task) => {
                                    let amt = agg;
                                    let amount = Number(task.amount);
                                    let totalPayments = getTotalPayments(task);
                                    if(task.complete) {
                                        amt += totalPayments > amount ? totalPayments : amount;
                                    } else if(task.payments.length) {
                                        amt += totalPayments;
                                    }
                                    return amt;
                                }, 0);
    viewModel.credit -= completedAmount;
    viewModel.total = viewModel.credit - viewModel.debit;
    return viewModel;
};

export const createTitleAmountViewModel = (entries) => {
    return pipe(
        map(pick(['title', 'amount', 'tag'])),
        uniq
    )(entries);
};

export const createPaymentsTitleAmountViewModel = (entries) => {
    return pipe(
        pluck('payments'),
        flatten,
        map(pick(['name', 'amount'])),
        groupBy(prop('name')),
        toPairs,
        map((pair) => take(5, pair[1])),
        flatten,
        uniq
    )(entries);
};

export const createTitlesViewModel = (entries) => {
    return pipe(
        pluck('title'),
        reject(isNil),
        map(split(',')),
        flatten,
        uniq
    )(entries);
};

export const createTagsViewModel = (entries) => {
    return pipe(
        pluck('tag'),
        reject(isNil),
        map(split(',')),
        flatten,
        uniq
    )(entries);
};

export const createTaskListPositionFactory = (state, action) => {
    return {
        ...state,
        taskList : {
            ...state.taskList,
            position : action.payload
        }
    };
};

export const createSearchLayoutFactory = (state, action) => {
    return {
        ...state,
        search : {
            ...state.search,
            ...action.payload
        }
    };
};

export const createEntriesLayoutFactory = (state, action) => {
    return {
        ...state,
        entries : {
            ...state.entries,
            ...action.payload
        }
    };
};

export const createAddEntryLayoutFactory = (state, action) => {
    return {
        ...state,
        entryDialogs : {
            ...state.entryDialogs,
            showAddEntry : action.payload.show,
            showEditEntry : false,
            showEditEntryPayments : false,
            entryType : action.payload.entryType,
            insertOrder : action.payload.entryOrder
        }
    };
};

export const createEditEntryLayoutFactory = (state, action) => {
    return {
        ...state,
        entryDialogs : {
            ...state.entryDialogs,
            showAddEntry : false,
            showEditEntry : action.payload,
            showEditEntryPayments : false,
        }
    }   
};

export const createEditEntryPaymentsLayoutFactory = (state, action) => {
    return {
        ...state,
        entryDialogs : {
            ...state.entryDialogs,
            showAddEntry : false,
            showEditEntry : false,
            showEditEntryPayments : action.payload
        }
    }   
};

export const createLoaderStateFactory = (state, action) => {
    return {
        ...state,
        loaders : {
            ...state.loaders,
            [action.type] : action.payload
        }
    };
};

export const createBalances = (entries) => {
    let balance = 0;
    const byOrder = ascend(prop('order'));
    entries = sort(byOrder, entries);
    return entries.reduce((agg, task) => {
        let { amount, type, ignore } = task;
        amount = Number(amount);
        let totalPayments = getTotalPayments(task);
        amount = totalPayments > 0 ? 0 : amount;
        let nextBal = type === 'CREDIT' ? balance + amount : balance - amount;
        if(ignore) {
            nextBal = NaN;
        } else {
            balance = Number(nextBal);
        }
        return {
            ...agg,
            [task.id] : nextBal
        };
    }, {});
};

export const createUpdateReportStateFactory = (state, action) => {
    let report = action.payload;
    let index = findIndex(propEq('name', report.name), state);
    return update(index, report, state);
};
